import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import type { FileResponse as VectorStoreFile } from "../../api/generated";
import { EmptyState } from "../common/EmptyState";

interface FileListProps {
  files: VectorStoreFile[];
  onDelete: (fileId: string) => void;
  onReindex: (fileId: string) => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusChip({
  status,
  error,
}: {
  status: string;
  error: string | null;
}) {
  switch (status) {
    case "pending":
      return (
        <Chip label="Pending" size="small" color="warning" variant="outlined" />
      );
    case "processing":
      return (
        <Chip
          label="Processing"
          size="small"
          color="info"
          variant="outlined"
          icon={<CircularProgress size={12} />}
        />
      );
    case "indexed":
      return (
        <Chip label="Indexed" size="small" color="success" variant="outlined" />
      );
    case "failed":
      return (
        <Tooltip title={error || "Processing failed"}>
          <Chip label="Failed" size="small" color="error" variant="outlined" />
        </Tooltip>
      );
    case "disabled":
      return (
        <Tooltip title={error || "Unsupported format"}>
          <Chip label="Disabled" size="small" variant="outlined" />
        </Tooltip>
      );
    default:
      return <Chip label={status} size="small" variant="outlined" />;
  }
}

export function FileList({ files, onDelete, onReindex }: FileListProps) {
  if (files.length === 0) {
    return (
      <EmptyState
        icon={<InsertDriveFileOutlinedIcon />}
        title="No files yet"
        description="Upload a document above to add it to this knowledge base."
      />
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Size</TableCell>
            <TableCell>Chunks</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((file) => (
            <TableRow key={file.id}>
              <TableCell>
                <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                  {file.filename}
                </Typography>
              </TableCell>
              <TableCell>{formatSize(file.size_bytes)}</TableCell>
              <TableCell>{file.chunk_count}</TableCell>
              <TableCell>
                <StatusChip status={file.status} error={file.error ?? null} />
              </TableCell>
              <TableCell align="right">
                {(file.status === "failed" || file.status === "disabled") && (
                  <Tooltip title="Re-index">
                    <IconButton size="small" onClick={() => onReindex(file.id)}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => onDelete(file.id)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
